import {
  Lightning, Plug, GearSix, Fan, SquaresFour, SlidersHorizontal, ThermometerHot,
  Drop, Magnet, BatteryCharging, Package, TreeStructure, BatteryFull, PuzzlePiece,
} from "@phosphor-icons/react";

export const ICONS = {
  lightning: Lightning,
  plug: Plug,
  gear: GearSix,
  fan: Fan,
  squares: SquaresFour,
  sliders: SlidersHorizontal,
  thermometer: ThermometerHot,
  drop: Drop,
  magnet: Magnet,
  batteryCharging: BatteryCharging,
  package: Package,
  tree: TreeStructure,
  battery: BatteryFull,
  puzzle: PuzzlePiece,
};

export const INDUSTRIES = [
  { key: "power_transformers", name: "Power Transformers", icon: "lightning", color: "var(--c-amber)", desc: "Oil-immersed & dry-type up to EHV class" },
  { key: "distribution_transformers", name: "Distribution Transformers", icon: "plug", color: "var(--c-accent)", desc: "Pole & pad-mounted up to 33kV" },
  { key: "electric_motors_ac", name: "Electric Motors AC", icon: "gear", color: "var(--c-green)", desc: "Induction & synchronous, LV to MV" },
  { key: "electric_generators", name: "Electric Generators", icon: "fan", color: "var(--c-red)", desc: "Synchronous generators & alternators" },
  { key: "switchgear_mv_hv", name: "Switchgear MV/HV", icon: "squares", color: "var(--c-accent)", desc: "MV/HV panels, breakers & bus systems" },
  { key: "control_panels_mcc", name: "Control Panels & MCC", icon: "sliders", color: "var(--c-green)", desc: "Motor control centers & PLC panels" },
  { key: "heat_exchangers", name: "Heat Exchangers", icon: "thermometer", color: "var(--c-red)", desc: "Shell & tube, plate, air-cooled" },
  { key: "pressure_vessels", name: "Pressure Vessels", icon: "drop", color: "var(--c-muted)", desc: "ASME/EN coded vessels & tanks" },
  { key: "industrial_pumps", name: "Industrial Pumps", icon: "drop", color: "var(--c-accent)", desc: "Centrifugal & positive displacement" },
  { key: "reactors_inductors", name: "Reactors & Inductors", icon: "magnet", color: "var(--c-amber)", desc: "Shunt, series & filter reactors" },
  { key: "capacitor_banks", name: "Capacitor Banks", icon: "batteryCharging", color: "var(--c-green)", desc: "PFC & harmonic filter banks" },
  { key: "electric_enclosures", name: "Electric Enclosures", icon: "package", color: "var(--c-muted)", desc: "Custom cabinets, kiosks & boxes" },
  { key: "cable_management", name: "Cable Management", icon: "tree", color: "var(--c-accent)", desc: "Trays, ladders & support systems" },
  { key: "battery_energy_storage", name: "Battery & Energy Storage", icon: "battery", color: "var(--c-green)", desc: "BESS containers & UPS systems" },
  { key: "gsu_transformers", name: "GSU Transformers", icon: "lightning", color: "var(--c-amber)", desc: "Generator step-up, GCB coordinated" },
  { key: "furnace_transformers", name: "Furnace Transformers (EAF)", icon: "lightning", color: "var(--c-red)", desc: "Arc & ladle furnace duty" },
  { key: "rectifier_transformers", name: "Rectifier Transformers", icon: "plug", color: "var(--c-green)", desc: "Harmonic-rated, OLTC + transductor" },
  { key: "converter_transformers_hvdc", name: "Converter Transformers (HVDC)", icon: "lightning", color: "var(--c-accent)", desc: "Valve winding with DC bias" },
  { key: "traction_transformers", name: "Traction Transformers", icon: "plug", color: "var(--c-accent)", desc: "Cyclic overload, 25 kV feeding" },
  { key: "rectifier_systems", name: "Rectifier Systems", icon: "plug", color: "var(--c-green)", desc: "Pulse number, ripple, harmonics, devices" },
  { key: "custom_industry", name: "Custom Industry", icon: "puzzle", color: "var(--c-muted)", desc: "Define your own spec schema" },
];

export const industryByKey = (key) => INDUSTRIES.find((i) => i.key === key) || INDUSTRIES[INDUSTRIES.length - 1];

export const MODULES = [
  { key: "document_intake", label: "Document Intake & AI Parsing", desc: "Upload client specs, AI extracts structured parameters", default: true },
  { key: "design_engine", label: "Design Calculation Engine", desc: "Industry-specific engineering calculations", default: true },
  { key: "optimization", label: "Design Optimization", desc: "Optimize cost, losses, weight and materials", default: true },
  { key: "validation", label: "Validation & Simulation Suite", desc: "Validate designs against industry standards", default: true },
  { key: "drawing_studio", label: "Drawing Studio", desc: "View, edit and generate manufacturing drawings", default: true },
  { key: "document_generation", label: "Document Generation", desc: "Auto-generate design reports and client documents", default: true },
  { key: "bom", label: "BOM Generator", desc: "Bill of materials from industry templates", default: true },
  { key: "client_portal", label: "Client Approval Portal", desc: "External client review and approval workflow", default: true },
  { key: "scm", label: "SCM & Vendor Management", desc: "Suppliers, RFQs and procurement tracking", default: false },
  { key: "handover", label: "Cross-Functional Handover", desc: "Structured engineering-to-manufacturing handover", default: false },
  { key: "analytics", label: "Advanced Analytics", desc: "Design performance, cycle times and trends", default: false },
];

export const DEFAULT_STAGES = [
  { key: "intake", name: "Intake", sla_days: 2, locked: true },
  { key: "design", name: "Design", sla_days: 5, locked: false },
  { key: "optimization", name: "Optimization", sla_days: 3, locked: false },
  { key: "validation", name: "Validation", sla_days: 3, locked: false },
  { key: "review", name: "Review", sla_days: 2, locked: false },
  { key: "handover", name: "Handover", sla_days: 1, locked: false },
  { key: "client_docs", name: "Client Docs", sla_days: 2, locked: false },
  { key: "client_approval", name: "Client Approval", sla_days: 5, locked: false },
  { key: "drawings", name: "Drawings", sla_days: 4, locked: false },
  { key: "bom", name: "BOM", sla_days: 2, locked: false },
  { key: "released", name: "Released", sla_days: 0, locked: true },
];

export const ROLES = [
  { key: "engineer", label: "Engineer" },
  { key: "lead", label: "Lead / Reviewer" },
  { key: "project_manager", label: "Project Manager" },
  { key: "scm_manager", label: "SCM Manager" },
  { key: "client", label: "Client (External)" },
  { key: "admin", label: "Admin" },
];

export const roleLabel = (key) => ROLES.find((r) => r.key === key)?.label || key;

export const PRIORITY_COLORS = { high: "var(--c-red)", medium: "var(--c-amber)", low: "var(--c-green)" };

export const daysSince = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  return Math.max(Math.floor((Date.now() - d.getTime()) / 86400000), 0);
};
